import React, { useState, useEffect } from "react"
import { useHistory } from "react-router-dom"
import { Container, Card, CardBody, Row, Col, Button, Input, Table, Badge } from "reactstrap"
import Breadcrumb from "components/Common/Breadcrumb"
import {
  getSeguradoras,
  createSeguradora,
  updateSeguradora,
  deleteSeguradora,
} from "helpers/seguradora_helper"
import InputMask from "react-input-mask"
import { FaEdit, FaTrash, FaPlus, FaLayerGroup } from "react-icons/fa"
import toastr from "toastr"
import "toastr/build/toastr.min.css"

const Seguradoras = () => {
  document.title = "Seguradoras | Blops Leads"
  const history = useHistory()

  const [seguradoras, setSeguradoras] = useState([])
  const [nome, setNome] = useState("")
  const [cnpj, setCnpj] = useState("")
  const [codigoSusep, setCodigoSusep] = useState("")
  const [editando, setEditando] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    carregarSeguradoras()
  }, [])

  const carregarSeguradoras = async () => {
    setLoading(true)
    try {
      const response = await getSeguradoras()
      // Filtrar apenas as ativas (não excluídas)
      const seguradorasAtivas = response.filter(s => !s.DataExclusao)
      setSeguradoras(seguradorasAtivas)
    } catch (error) {
      console.error("Erro ao carregar seguradoras:", error)
      toastr.error("Erro ao carregar seguradoras", "Erro")
    } finally {
      setLoading(false)
    }
  }

  const handleSalvar = async () => {
    if (!nome.trim()) {
      toastr.warning("Por favor, informe o nome da seguradora", "Atenção")
      return
    }

    setLoading(true)
    try {
      if (editando) {
        await updateSeguradora(editando.Id, { Nome: nome, Cnpj: cnpj, CodigoSusep: codigoSusep })
        toastr.success("Seguradora atualizada com sucesso!", "Sucesso")
      } else {
        await createSeguradora({ Nome: nome, Cnpj: cnpj, CodigoSusep: codigoSusep })
        toastr.success("Seguradora criada com sucesso!", "Sucesso")
      }
      setNome("")
      setCnpj("")
      setCodigoSusep("")
      setEditando(null)
      carregarSeguradoras()
    } catch (error) {
      console.error("Erro ao salvar seguradora:", error)
      if (error.response?.status === 400) {
        toastr.error(error.response.data.message || "Dados inválidos", "Erro")
      } else {
        toastr.error("Erro ao salvar seguradora", "Erro")
      }
    } finally {
      setLoading(false)
    }
  }

  const handleEditar = seguradora => {
    setNome(seguradora.Nome)
    setCnpj(seguradora.Cnpj || "")
    setCodigoSusep(seguradora.CodigoSusep || "")
    setEditando(seguradora)
    // Scroll para o topo do formulário
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  const handleCancelar = () => {
    setNome("")
    setCnpj("")
    setCodigoSusep("")
    setEditando(null)
  }

  const handleExcluir = async id => {
    if (window.confirm("Deseja realmente excluir esta seguradora?")) {
      setLoading(true)
      try {
        await deleteSeguradora(id)
        toastr.success("Seguradora excluída com sucesso!", "Sucesso")
        carregarSeguradoras()
      } catch (error) {
        console.error("Erro ao excluir seguradora:", error)
        if (error.response?.status === 400) {
          toastr.error(
            error.response.data.message || "Não foi possível excluir",
            "Erro"
          )
        } else {
          toastr.error("Erro ao excluir seguradora", "Erro")
        }
      } finally {
        setLoading(false)
      }
    }
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb
            title="Cadastros"
            breadcrumbItem="Seguradoras"
          />

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <h4 className="card-title mb-4">
                    {editando ? "Editar Seguradora" : "Nova Seguradora"}
                  </h4>

                  {editando && (
                    <div className="alert alert-info" role="alert">
                      <i className="mdi mdi-alert-circle-outline me-2"></i>
                      Você está editando a seguradora: <strong>{editando.Nome}</strong>
                    </div>
                  )}

                  <Row className="mb-3">
                    <Col md={4} className="mb-2">
                      <label className="form-label">Nome *</label>
                      <Input
                        type="text"
                        placeholder="Nome da Seguradora"
                        value={nome}
                        onChange={e => setNome(e.target.value)}
                        disabled={loading}
                      />
                    </Col>
                    <Col md={3} className="mb-2">
                      <label className="form-label">CNPJ</label>
                      <InputMask
                        mask="99.999.999/9999-99"
                        className="form-control"
                        placeholder="00.000.000/0001-00"
                        value={cnpj}
                        onChange={e => setCnpj(e.target.value)}
                        disabled={loading}
                      />
                    </Col>
                    <Col md={3} className="mb-2">
                      <label className="form-label">Código SUSEP</label>
                      <Input
                        type="text"
                        placeholder="Código SUSEP"
                        value={codigoSusep}
                        onChange={e => setCodigoSusep(e.target.value)}
                        disabled={loading}
                      />
                    </Col>
                    <Col md={2} className="mb-2 d-flex align-items-end gap-2">
                      <Button
                        color="primary"
                        onClick={handleSalvar}
                        disabled={loading}
                      >
                        <FaPlus className="me-1" />
                        {editando ? "Atualizar" : "Criar"}
                      </Button>
                      {editando && (
                        <Button
                          color="secondary"
                          onClick={handleCancelar}
                          disabled={loading}
                        >
                          Cancelar
                        </Button>
                      )}
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <h4 className="card-title mb-4">Lista de Seguradoras</h4>

                  {loading ? (
                    <div className="text-center">
                      <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">Carregando...</span>
                      </div>
                    </div>
                  ) : seguradoras.length === 0 ? (
                    <div className="text-center text-muted">
                      <p>Nenhuma seguradora cadastrada</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <Table className="table table-striped table-hover mb-0">
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: "60px" }}>ID</th>
                            <th>Nome</th>
                            <th>CNPJ</th>
                            <th>Código SUSEP</th>
                            <th style={{ width: "130px" }}>Template Ativo</th>
                            <th style={{ width: "160px" }} className="text-center">Ações</th>
                          </tr>
                        </thead>
                        <tbody>
                          {seguradoras.map(s => (
                            <tr key={s.Id}>
                              <td>{s.Id}</td>
                              <td>{s.Nome}</td>
                              <td>{s.Cnpj || "—"}</td>
                              <td>{s.CodigoSusep || "—"}</td>
                              <td>
                                {s.Templates?.find(t => t.Ativo) ? (
                                  <Badge color="success">
                                    {s.Templates.find(t => t.Ativo).Nome}
                                  </Badge>
                                ) : (
                                  <small className="text-muted">—</small>
                                )}
                              </td>
                              <td className="text-center">
                                <Button
                                  color="secondary"
                                  size="sm"
                                  className="me-1"
                                  onClick={() => history.push(`/seguradoras/${s.Id}/templates`)}
                                  title="Gerenciar Templates"
                                >
                                  <FaLayerGroup />
                                </Button>
                                <Button
                                  color="info"
                                  size="sm"
                                  className="me-1"
                                  onClick={() => handleEditar(s)}
                                  title="Editar"
                                >
                                  <FaEdit />
                                </Button>
                                <Button
                                  color="danger"
                                  size="sm"
                                  onClick={() => handleExcluir(s.Id)}
                                  title="Excluir"
                                >
                                  <FaTrash />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default Seguradoras

